import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Breadcrumbs, Button, Card, CardActionArea, CardContent,
  Typography, Grid2, Chip, Box, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import AppShell from './components/AppShell';
import api from './services/api';
import { useAuth } from './context/AuthContext';

const CARD_SX = {
  elevation: 0,
  sx: {
    border: '1px solid #e0e0e0',
    borderRadius: 3,
    transition: 'box-shadow 0.2s, transform 0.2s',
    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', transform: 'translateY(-2px)' },
  },
};

const ProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/projects/${projectId}/`).then((res) => {
      setProject(res.data);
      setLoading(false);
    });
    // Bump this user's "last opened" stamp for the Recent strip on Home.
    // Fire-and-forget: a failure here must never affect the page.
    api.post(`/projects/${projectId}/visit/`).catch(() => {});
  }, [projectId]);

  const handleDeleteProject = async () => {
    if (!window.confirm(`Delete project "${project.name}"? This cannot be undone.`)) return;
    await api.delete(`/projects/${projectId}/`);
    navigate('/');
  };

  const handleDeleteSurvey = async (survey) => {
    if (!window.confirm(`Delete survey "${survey.name}"? This cannot be undone.`)) return;
    await api.delete(`/surveys/${survey.id}/`);
    setProject((prev) => ({ ...prev, surveys: prev.surveys.filter((s) => s.id !== survey.id) }));
  };

  if (loading) return null;
  if (!project) return <Typography sx={{ mt: 4, ml: 4 }}>Project not found.</Typography>;

  const surveys = project.surveys ?? [];

  return (
    <AppShell>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Breadcrumbs separator=">">
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
          <Typography color="textPrimary">{project.name}</Typography>
        </Breadcrumbs>
        {user?.role === 'stack' && (
          <Box display="flex" gap={1}>
            <Button
              component={Link}
              to={`/project/${projectId}/add-survey`}
              variant="contained"
              startIcon={<AddIcon />}
              size="small"
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Add Survey
            </Button>
            <Tooltip title="Delete project">
              <IconButton size="small" color="error" onClick={handleDeleteProject}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700} sx={{ mb: 0.5 }}>
        {project.name}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
        {surveys.length} survey wave{surveys.length !== 1 ? 's' : ''}
      </Typography>

      {surveys.length === 0 ? (
        <Typography color="textSecondary">
          No surveys yet.{user?.role === 'stack' && ' Add the first one above.'}
        </Typography>
      ) : (
        <Grid2 container spacing={3}>
          {surveys.map((survey) => (
            <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={survey.id}>
              {user?.role === 'stack' ? (
                <Card {...CARD_SX} sx={{ ...CARD_SX.sx, position: 'relative' }}>
                  <CardActionArea component={Link} to={`/survey/${survey.id}`} sx={{ p: 1 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                        <Box sx={{ bgcolor: 'primary.light', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <BarChartRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={600} noWrap>
                          {survey.name}
                        </Typography>
                      </Box>
                      <Chip label={`ID: ${survey.alchemer_survey_id}`} size="small" variant="outlined" />
                    </CardContent>
                  </CardActionArea>
                  <Tooltip title="Delete survey">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.preventDefault(); handleDeleteSurvey(survey); }}
                      sx={{ position: 'absolute', top: 6, right: 6 }}
                    >
                      <DeleteRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Card>
              ) : (
                <Card {...CARD_SX}>
                  <CardActionArea component={Link} to={`/survey/${survey.id}/quota-monitoring`} sx={{ p: 1 }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                        <Box sx={{ bgcolor: 'primary.light', borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <LeaderboardRoundedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" fontFamily="'Work Sans', sans-serif" fontWeight={600}>{survey.name}</Typography>
                          <Typography variant="body2" color="textSecondary">Quota monitoring</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              )}
            </Grid2>
          ))}
        </Grid2>
      )}
    </AppShell>
  );
};

export default ProjectPage;
